/**
 * Every sound in NabuSu is synthesised in the browser.
 *
 * That is a deliberate constraint, not a shortcut: an app for 3am has to work
 * with no signal, no storage quota and no CDN. Nothing here downloads. The whole
 * sound library is a few hundred lines of maths and it will still play in ten
 * years on a plane.
 */

export type SoundId = 'off' | 'brown' | 'rain' | 'drone' | 'hush' | 'heart'

let ctx: AudioContext | null = null
let master: GainNode | null = null
let current: { id: SoundId; stop: () => void } | null = null
let fadeTimer: number | undefined

function audio(): AudioContext {
  if (!ctx) {
    ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    master = ctx.createGain()
    master.gain.value = 0
    master.connect(ctx.destination)
  }
  return ctx
}

/** iOS suspends the context until a user gesture; call this from any tap. */
export async function unlock(): Promise<void> {
  const c = audio()
  if (c.state === 'suspended') await c.resume()
}

/** Noise buffers are expensive to build; make one long loop and reuse it. */
let noiseBuffer: AudioBuffer | null = null
function makeNoise(c: AudioContext): AudioBuffer {
  if (noiseBuffer) return noiseBuffer
  const seconds = 6
  const len = c.sampleRate * seconds
  const buf = c.createBuffer(1, len, c.sampleRate)
  const data = buf.getChannelData(0)

  // Brown noise: integrated white noise. Deeper and far less hissy than white,
  // which matters when it plays for eight hours next to someone's head.
  let last = 0
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1
    last = (last + 0.02 * white) / 1.02
    data[i] = last * 3.5
  }

  // Cross-fade the tail into the head so the loop point is inaudible.
  const fade = Math.floor(c.sampleRate * 0.5)
  for (let i = 0; i < fade; i++) {
    const k = i / fade
    data[len - fade + i] = data[len - fade + i]! * (1 - k) + data[i]! * k
  }

  noiseBuffer = buf
  return buf
}

function noiseSource(c: AudioContext): AudioBufferSourceNode {
  const src = c.createBufferSource()
  src.buffer = makeNoise(c)
  src.loop = true
  return src
}

function buildBrown(c: AudioContext, out: AudioNode): () => void {
  const src = noiseSource(c)
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 500
  src.connect(lp).connect(out)
  src.start()
  return () => src.stop()
}

function buildRain(c: AudioContext, out: AudioNode): () => void {
  // Body of the rain: bandpassed noise, slowly wandering so it never sits still.
  const src = noiseSource(c)
  const bp = c.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = 1200
  bp.Q.value = 0.6

  const hp = c.createBiquadFilter()
  hp.type = 'highpass'
  hp.frequency.value = 300

  const wander = c.createOscillator()
  wander.frequency.value = 0.05
  const wanderGain = c.createGain()
  wanderGain.gain.value = 400
  wander.connect(wanderGain).connect(bp.frequency)
  wander.start()

  src.connect(hp).connect(bp).connect(out)
  src.start()

  // Individual drops, sparse and random, so the ear keeps finding detail.
  let alive = true
  const drop = (): void => {
    if (!alive) return
    const o = c.createOscillator()
    const g = c.createGain()
    o.type = 'sine'
    o.frequency.value = 900 + Math.random() * 2600
    const t = c.currentTime
    g.gain.setValueAtTime(0, t)
    g.gain.linearRampToValueAtTime(0.035 + Math.random() * 0.03, t + 0.004)
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08 + Math.random() * 0.1)
    o.connect(g).connect(out)
    o.start(t)
    o.stop(t + 0.25)
    setTimeout(drop, 40 + Math.random() * 320)
  }
  drop()

  return () => {
    alive = false
    src.stop()
    wander.stop()
  }
}

function buildDrone(c: AudioContext, out: AudioNode): () => void {
  // A low chord tuned near 55Hz. Slight detune between voices produces a slow
  // beat that the body tends to follow downward.
  const freqs = [55, 82.5, 110, 164.81]
  const oscs: OscillatorNode[] = []
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 700
  lp.connect(out)

  freqs.forEach((f, i) => {
    for (const detune of [-4, 4]) {
      const o = c.createOscillator()
      o.type = i === 3 ? 'sine' : 'triangle'
      o.frequency.value = f
      o.detune.value = detune
      const g = c.createGain()
      g.gain.value = (i === 3 ? 0.06 : 0.14) / 2
      o.connect(g).connect(lp)
      o.start()
      oscs.push(o)
    }
  })

  // Very slow swell, roughly one cycle per 20 seconds — near a resting breath.
  const lfo = c.createOscillator()
  lfo.frequency.value = 0.05
  const lfoGain = c.createGain()
  lfoGain.gain.value = 200
  lfo.connect(lfoGain).connect(lp.frequency)
  lfo.start()
  oscs.push(lfo)

  return () => oscs.forEach((o) => o.stop())
}

function buildHush(c: AudioContext, out: AudioNode): () => void {
  // Noise shaped like a long slow exhale, about 5.5 breaths per minute.
  const src = noiseSource(c)
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 900
  const shaper = c.createGain()
  shaper.gain.value = 0.35

  const lfo = c.createOscillator()
  lfo.type = 'sine'
  lfo.frequency.value = 0.092
  const lfoGain = c.createGain()
  lfoGain.gain.value = 0.25
  lfo.connect(lfoGain).connect(shaper.gain)
  lfo.start()

  src.connect(lp).connect(shaper).connect(out)
  src.start()
  return () => {
    src.stop()
    lfo.stop()
  }
}

function buildHeart(c: AudioContext, out: AudioNode): () => void {
  // 52 bpm — below a resting rate, so following it slows you down.
  const bpm = 52
  const interval = 60 / bpm
  let alive = true

  const thump = (at: number, level: number): void => {
    const o = c.createOscillator()
    const g = c.createGain()
    o.type = 'sine'
    o.frequency.setValueAtTime(72, at)
    o.frequency.exponentialRampToValueAtTime(38, at + 0.14)
    g.gain.setValueAtTime(0, at)
    g.gain.linearRampToValueAtTime(level, at + 0.02)
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.3)
    o.connect(g).connect(out)
    o.start(at)
    o.stop(at + 0.35)
  }

  const beat = (): void => {
    if (!alive) return
    const t = c.currentTime
    thump(t, 0.5) // lub
    thump(t + 0.22, 0.3) // dub
    setTimeout(beat, interval * 1000)
  }
  beat()

  return () => {
    alive = false
  }
}

const builders: Record<Exclude<SoundId, 'off'>, (c: AudioContext, out: AudioNode) => () => void> = {
  brown: buildBrown,
  rain: buildRain,
  drone: buildDrone,
  hush: buildHush,
  heart: buildHeart,
}

export function currentSound(): SoundId {
  return current?.id ?? 'off'
}

export async function play(id: SoundId, volume: number): Promise<void> {
  if (id === 'off') return stop()
  await unlock()
  const c = audio()
  if (current) {
    current.stop()
    current = null
  }
  clearTimeout(fadeTimer)
  const stopFn = builders[id](c, master!)
  current = { id, stop: stopFn }
  setVolume(volume, 1.5)
}

export function stop(fadeSeconds = 1.2): void {
  if (!ctx || !master) return
  clearTimeout(fadeTimer)
  const t = ctx.currentTime
  master.gain.cancelScheduledValues(t)
  master.gain.setValueAtTime(master.gain.value, t)
  master.gain.linearRampToValueAtTime(0, t + fadeSeconds)
  const ref = current
  current = null
  fadeTimer = window.setTimeout(() => ref?.stop(), fadeSeconds * 1000 + 60)
}

export function setVolume(v: number, rampSeconds = 0.25): void {
  if (!ctx || !master) return
  const t = ctx.currentTime
  master.gain.cancelScheduledValues(t)
  master.gain.setValueAtTime(master.gain.value, t)
  // Perceived loudness is closer to the square of the slider than the slider itself.
  master.gain.linearRampToValueAtTime(Math.max(0, Math.min(1, v)) ** 2 * 0.9, t + rampSeconds)
}

/** Fade to silence over `minutes`, then stop. Used as a sleep timer. */
export function fadeOutAfter(minutes: number): void {
  if (!ctx || !master || !current) return
  clearTimeout(fadeTimer)
  const t = ctx.currentTime
  const seconds = minutes * 60
  master.gain.cancelScheduledValues(t)
  master.gain.setValueAtTime(master.gain.value, t)
  // Hold most of the way, then fade over the final quarter — a fade that starts
  // immediately is noticeable, and noticing is what keeps people awake.
  master.gain.setValueAtTime(master.gain.value, t + seconds * 0.75)
  master.gain.linearRampToValueAtTime(0, t + seconds)
  fadeTimer = window.setTimeout(() => stop(0.1), seconds * 1000 + 200)
}

/** A soft marker tone for breath phase changes. Quiet enough not to jolt. */
export async function cue(freq = 440, level = 0.08): Promise<void> {
  await unlock()
  const c = audio()
  const o = c.createOscillator()
  const g = c.createGain()
  o.type = 'sine'
  o.frequency.value = freq
  const t = c.currentTime
  g.gain.setValueAtTime(0, t)
  g.gain.linearRampToValueAtTime(level, t + 0.05)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.6)
  o.connect(g).connect(c.destination)
  o.start(t)
  o.stop(t + 0.7)
}
